import React, { FC, useCallback, useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useAppDispatch } from "src/redux/storeHooks";
import { deleteRecipeAsync, duplicateRecipeAsync, getRecipesAsyncUsingPlatformId } from "src/redux/actions/templateAndPricingActions";
import useAppToast from "src/hooks/useAppToast";
import { useDebounce } from "src/marketConnect/helpers";
import { usePermission } from "src/hooks/usePermission";
import { useUserHavingWriteAccess } from "src/utils/useUserHavingWriteAccess";
import { KTIcon } from "src/marketConnect/helpers";

export interface SavedQuickListItem {
  id: number;
  name: string;
  status?: "ACTIVE" | "DRAFT";
  check_data_conjunction?: string;
  created_at?: string;
  updated_at?: string;
  title_description_template?: number;
  price_template?: number;
  category_template?: number;
  mapping_data?: any[];
  other_templates?: any[];
  platform?: number;
}

interface SavedQuickListsPanelProps {
  platformId: number | undefined;
  onEdit: (item: SavedQuickListItem) => void | Promise<void>;
  refreshTrigger: number;
  onDeleteCurrentEdit?: (deletedId: number) => void;
  onCreateClick?: () => void;
}

type SortField = "created_at" | "updated_at";
type SortOrder = "asc" | "desc";

const sortListItems = (
  items: SavedQuickListItem[],
  field: SortField,
  order: SortOrder
): SavedQuickListItem[] => {
  const getTime = (value?: string) => {
    if (!value) return 0;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? 0 : time;
  };

  return [...items].sort((a, b) => {
    const diff = getTime(a[field]) - getTime(b[field]);
    return order === "desc" ? -diff : diff;
  });
};

const SavedQuickListsPanel: FC<SavedQuickListsPanelProps> = ({
  platformId,
  onEdit,
  refreshTrigger,
  onDeleteCurrentEdit,
  onCreateClick,
}) => {
  const dispatch = useAppDispatch();
  const { successToast, errorToast } = useAppToast();
  const { isWrite, isDelete } = usePermission();
  const { isUserHavingWriteAccess } = useUserHavingWriteAccess();

  const [lists, setLists] = useState<SavedQuickListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 500);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    count: 0,
    next: null as string | null,
    previous: null as string | null,
  });

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [copyingId, setCopyingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [sort, setSort] = useState<{ field: SortField; order: SortOrder }>({
    field: "updated_at",
    order: "desc",
  });

  const fetchLists = useCallback(
    (page = 1, sortOverride?: { field: SortField; order: SortOrder }) => {
      if (!platformId) return;
      const activeSort = sortOverride ?? sort;
      setLoading(true);
      dispatch(
        getRecipesAsyncUsingPlatformId({
          platformId: platformId + "",
          otherParams: {
            page,
            limit: pagination.limit,
            search: debouncedSearch ?? "",
            type: "QUICKLIST",
            sort_by: activeSort.field,
            sort_order: activeSort.order,
          },
        })
      )
        .then((response: any) => {
          const payload = response?.payload;
          setLists(sortListItems(payload?.results ?? [], activeSort.field, activeSort.order));
          setPagination((prev) => ({
            ...prev,
            page,
            count: payload?.count ?? 0,
            next: payload?.next ?? null,
            previous: payload?.previous ?? null,
          }));
        })
        .catch((err) => {
          console.error("Failed to fetch saved Quick Lists:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [platformId, debouncedSearch, pagination.limit, dispatch, sort]
  );

  useEffect(() => {
    fetchLists(1);
  }, [debouncedSearch, platformId, refreshTrigger, sort.field, sort.order]);

  const handleCopy = async (id: number) => {
    if (copyingId !== null || editingId !== null) return;
    setCopyingId(id);
    try {
      const result: any = await dispatch(duplicateRecipeAsync({ recipeId: id }));
      if (result?.payload?.error || result?.error) {
        errorToast("Failed to copy Listing Profile.");
      } else {
        successToast("Listing Profile copied successfully!");
        fetchLists(pagination.page);
      }
    } catch (err) {
      errorToast("Unexpected error during copy.");
    } finally {
      setCopyingId(null);
    }
  };

  const handleEdit = async (item: SavedQuickListItem) => {
    if (copyingId !== null || editingId !== null) return;
    setEditingId(item.id);
    try {
      await onEdit(item);
    } catch (err) {
      console.error("Failed to open Listing Profile for edit:", err);
      errorToast("Failed to load Listing Profile for edit.");
    } finally {
      setEditingId(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    setDeleting(true);
    try {
      const result: any = await dispatch(
        deleteRecipeAsync({ templateMappingId: confirmDeleteId + "", type: "quicklist" })
      );
      if (result?.payload?.error || result?.error) {
        errorToast("Failed to delete Listing Profile.");
      } else {
        successToast("Listing Profile deleted successfully.");
        if (onDeleteCurrentEdit) {
          onDeleteCurrentEdit(confirmDeleteId);
        }
        setConfirmDeleteId(null);
        fetchLists(pagination.page);
      }
    } catch (err) {
      errorToast("Unexpected error during delete.");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusClass = (status?: string) =>
    status === "DRAFT"
      ? "product-listing__status-badge product-listing__status-badge--draft"
      : "product-listing__status-badge product-listing__status-badge--active";

  const handleSort = (field: SortField) => {
    setSort((prev) => ({
      field,
      order: prev.field === field && prev.order === "desc" ? "asc" : "desc",
    }));
  };

  const renderSortIcon = (field: SortField) => {
    if (sort.field !== field) return null;
    return (
      <i
        className={`bi ${
          sort.order === "asc" ? "bi-sort-alpha-down" : "bi-sort-alpha-down-alt"
        } product-listing__sort-icon ms-1`}
        aria-hidden="true"
      />
    );
  };

  const renderActionSpinner = (variant: "info" | "primary") => (
    <Spinner
      animation="border"
      size="sm"
      variant={variant}
      style={{ width: "14px", height: "14px" }}
    />
  );

  const rowActionBusy = copyingId !== null || editingId !== null;

  return (
    <div className="product-listing__card mb-4">
      <div className="product-listing__card-header">
        <h2 className="product-listing__card-title">Saved Listing Profiles</h2>
        <div className="product-listing__card-toolbar">
          <div className="product-listing__search-wrap">
            <i className="bi bi-search" aria-hidden="true" />
            <input
              type="text"
              className="form-control product-listing__search-input"
              placeholder="Search profiles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search profiles"
            />
          </div>
          {onCreateClick && isWrite && (
            <Button
              variant="primary"
              size="sm"
              className="product-listing__toolbar-btn fw-semibold"
              onClick={onCreateClick}
            >
              + Create Listing Profile
            </Button>
          )}
        </div>
      </div>

      <div className="product-listing__table-wrap">
        {loading ? (
          <div className="d-flex align-items-center justify-content-center py-5">
            <Spinner animation="border" size="sm" variant="primary" />
            <span className="ms-2 text-muted">Loading saved Listing Profiles...</span>
          </div>
        ) : lists.length === 0 ? (
          <div className="text-center text-muted py-5 fs-7">
            No saved Listing Profiles found for this platform.
          </div>
        ) : (
          <div className="table-responsive">
          <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3 product-listing__table mb-0">
            <colgroup>
              <col className="product-listing__col-name" />
              <col className="product-listing__col-status" />
              <col className="product-listing__col-date" />
              <col className="product-listing__col-date" />
              <col className="product-listing__col-actions" />
            </colgroup>
            <thead>
              <tr className="fw-bold text-muted">
                <th>Profile Name</th>
                <th>Status</th>
                <th
                  className="product-listing__sortable-th"
                  onClick={() => handleSort("created_at")}
                  aria-sort={sort.field === "created_at" ? (sort.order === "asc" ? "ascending" : "descending") : "none"}
                >
                  Created
                  {renderSortIcon("created_at")}
                </th>
                <th
                  className="product-listing__sortable-th"
                  onClick={() => handleSort("updated_at")}
                  aria-sort={sort.field === "updated_at" ? (sort.order === "asc" ? "ascending" : "descending") : "none"}
                >
                  Updated
                  {renderSortIcon("updated_at")}
                </th>
                <th className="product-listing__col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lists.map((item) => {
                const isRowEditing = editingId === item.id;
                const isRowCopying = copyingId === item.id;
                const isRowBusy = isRowEditing || isRowCopying;

                return (
                  <tr
                    key={item.id}
                    className={isRowBusy ? "product-listing__row--busy" : undefined}
                    aria-busy={isRowBusy}
                  >
                  <td className="text-dark fw-normal product-listing__col-name">
                    {isWrite && isUserHavingWriteAccess() ? (
                      isRowEditing ? (
                        <span className="product-listing__list-name-text product-listing__list-name-text--loading">
                          {item.name}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="product-listing__list-link fw-semibold text-info text-decoration-none"
                          disabled={rowActionBusy}
                          onClick={() => handleEdit(item)}
                        >
                          {item.name}
                        </button>
                      )
                    ) : (
                      <span className="product-listing__list-name-text">{item.name}</span>
                    )}
                  </td>
                  <td className="text-dark fw-normal product-listing__col-status">
                    <span className={getStatusClass(item.status)}>
                      {(item.status ?? "ACTIVE").toLowerCase()}
                    </span>
                  </td>
                  <td className="product-listing__date-cell fw-normal product-listing__col-date">{formatDate(item.created_at)}</td>
                  <td className="product-listing__date-cell fw-normal product-listing__col-date">{formatDate(item.updated_at)}</td>
                  <td className="text-dark fw-normal product-listing__col-actions">
                    {confirmDeleteId === item.id ? (
                      <div className="product-listing__actions">
                        <span className="fs-8 text-danger fw-semibold me-1">Delete?</span>
                        <Button
                          size="sm"
                          variant="danger"
                          className="py-0 px-2 fs-8"
                          disabled={deleting}
                          onClick={handleDelete}
                        >
                          {deleting ? "..." : "Yes"}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="py-0 px-2 fs-8"
                          onClick={() => setConfirmDeleteId(null)}
                        >
                          No
                        </Button>
                      </div>
                    ) : (
                      <div className="product-listing__actions">
                        {isWrite && isUserHavingWriteAccess() && (
                          <button
                            type="button"
                            className="btn btn-icon btn-bg-light btn-color-info product-listing__action-btn"
                            title="Copy Listing Profile"
                            disabled={rowActionBusy}
                            onClick={() => handleCopy(item.id)}
                          >
                            {copyingId === item.id ? (
                              renderActionSpinner("info")
                            ) : (
                              <KTIcon iconName="copy" className="fs-4" />
                            )}
                          </button>
                        )}
                        {isWrite && isUserHavingWriteAccess() && (
                          <button
                            type="button"
                            className="btn btn-icon btn-bg-light btn-color-primary product-listing__action-btn"
                            title="Edit Listing Profile"
                            disabled={rowActionBusy}
                            onClick={() => handleEdit(item)}
                          >
                            {editingId === item.id ? (
                              renderActionSpinner("primary")
                            ) : (
                              <KTIcon iconName="pencil" className="fs-4" />
                            )}
                          </button>
                        )}
                        {isDelete && isUserHavingWriteAccess() && (
                          <button
                            type="button"
                            className="btn btn-icon btn-bg-light btn-color-danger product-listing__action-btn"
                            title="Delete Listing Profile"
                            onClick={() => setConfirmDeleteId(item.id)}
                          >
                            <KTIcon iconName="trash" className="fs-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {pagination.count > 0 && !loading && (
        <div className="product-listing__pagination">
          <span className="text-muted fs-7">
            Showing{" "}
            <strong className="text-dark">
              {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.count)}
            </strong>{" "}
            to{" "}
            <strong className="text-dark">
              {Math.min(pagination.page * pagination.limit, pagination.count)}
            </strong>{" "}
            of <strong className="text-dark">{pagination.count}</strong> entries
          </span>
          <div className="d-flex align-items-center gap-1">
            <Button
              variant="outline-secondary"
              size="sm"
              className="d-inline-flex align-items-center gap-1"
              disabled={!pagination.previous}
              onClick={() => fetchLists(pagination.page - 1)}
            >
              <i className="bi bi-chevron-left fs-8" /> Previous
            </Button>
            <span className="badge bg-light text-primary border px-2 py-2 fw-semibold fs-8">
              Page {pagination.page} of {Math.ceil(pagination.count / pagination.limit)}
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              className="d-inline-flex align-items-center gap-1"
              disabled={!pagination.next}
              onClick={() => fetchLists(pagination.page + 1)}
            >
              Next <i className="bi bi-chevron-right fs-8" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedQuickListsPanel;
